import { useEffect, useState } from 'react';
import { Alert, Badge, Card, Col, Collapse, Row, Space, Spin, Statistic, Tag, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import { GRADE_COLORS, getSharedReport, PRIORITY_LABEL, ReportData } from '../../api/report';

/**
 * 公开分享报告页（M7，无需登录）
 * 通过 /report/share/:token 访问，7 天有效
 */
export default function SharePage() {
  const { token = '' } = useParams();
  const [data, setData] = useState<ReportData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSharedReport(token)
      .then(setData)
      .catch((e) => setError(e.response?.data?.message ?? '链接无效或已过期'));
  }, [token]);

  if (error) {
    return (
      <Card>
        <Alert type="warning" showIcon message="无法查看报告" description={error} />
      </Card>
    );
  }
  if (!data) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16, fontSize: 15, color: '#595959' }}>正在加载报告…</div>
      </div>
    );
  }

  const { diagnosis, score } = data;
  const dims = score.dimensionDetails;

  return (
    <Card>
      <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Typography.Title level={3} style={{ margin: 0 }}>
            {diagnosis.companyName || '企业'}数字化健康度体检报告
          </Typography.Title>
          <Typography.Text type="secondary">
            分享于 {new Date(data.diagnosis.submittedAt ?? new Date()).toLocaleString('zh-CN')}
          </Typography.Text>
        </Col>
        <Col>
          <Badge.Ribbon text={`${score.grade} 级 · ${score.gradeLabel}`} color={GRADE_COLORS[score.grade] ?? 'blue'}>
            <Card size="small">
              <Statistic title="健康总分" value={score.totalScore} suffix="/100" valueStyle={{ color: GRADE_COLORS[score.grade] }} />
            </Card>
          </Badge.Ribbon>
        </Col>
      </Row>

      <Typography.Title level={5}>五维得分</Typography.Title>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {dims.map((d) => (
          <Col xs={12} md={8} key={d.dimension}>
            <Card size="small">
              <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                <Tag>{d.name}</Tag>
                <b style={{ fontSize: 18 }}>{d.score ?? '—'}</b>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>

      <Typography.Title level={5}>改进建议</Typography.Title>
        {data.aiSuggestions ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: '#f9f0ff', border: '1px solid #d3adf7', borderRadius: 10, padding: '12px 16px' }}>
              <Typography.Text strong style={{ color: '#531dab' }}>总体结论</Typography.Text>
              <div style={{ fontSize: 14, lineHeight: 1.8, marginTop: 4 }}>{data.aiSuggestions.overview}</div>
            </div>
            <div style={{ background: '#fffbe6', border: '1px solid #ffd666', borderRadius: 10, padding: '12px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Typography.Text strong style={{ color: '#d46b08' }}>🎯 最优先做的 3 件事</Typography.Text>
                <Tag color="gold">按此顺序执行</Tag>
              </div>
              {data.aiSuggestions.top3.map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', fontSize: 13, lineHeight: 1.6 }}>
                  <span
                    style={{
                      background: '#d46b08',
                      color: '#fff',
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </span>
                  <span>{t}</span>
                </div>
              ))}
            </div>
            <Typography.Text strong style={{ fontSize: 14 }}>完整建议</Typography.Text>
            {data.aiSuggestions.priorities.map((p, i) => {
              const tm = p.timeline ? (p.timeline.match(/\d+/) ? parseInt(p.timeline.match(/\d+/)![0], 10) : 99) : 99;
              const tColor = tm <= 3 ? '#f5222d' : tm <= 6 ? '#fa8c16' : '#52c41a';
              const mIssues = score.moduleSummaries.find((mm) => mm.name === p.module)?.issues ?? [];
              return (
                <div
                  key={p.module}
                  style={{
                    borderLeft: `4px solid ${tColor}`,
                    borderTop: '1px solid #f0f0f0',
                    borderRight: '1px solid #f0f0f0',
                    borderBottom: '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: '10px 14px',
                    background: '#fff',
                  }}
                >
                  <div style={{ fontSize: 13.5, lineHeight: 1.7 }}>
                    <b>建议 {i + 1}｜{p.module}：</b>
                    {p.suggestion}
                  </div>
                  {p.benefit ? (
                    <div style={{ fontSize: 12.5, color: '#389e0d', marginTop: 4, lineHeight: 1.6 }}>💡 预期收益：{p.benefit}</div>
                  ) : null}
                  <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                    {p.timeline ? (
                      <span style={{ fontSize: 11, color: tColor, background: '#fff1f0', padding: '1px 8px', borderRadius: 10 }}>{p.timeline}</span>
                    ) : null}
                    {mIssues.length > 0 ? (
                      <span style={{ fontSize: 11, color: '#8c8c8c', background: '#fafafa', padding: '1px 8px', borderRadius: 10 }}>
                        依据：{mIssues.map((it) => (it.questionNumber != null ? `第${it.questionNumber}题` : it.indicator)).join('·')}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
            <Collapse
              ghost
              size="small"
              items={[
                {
                  key: 'detail',
                  label: '查看全部短板明细（每题得分）',
                  children: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {score.moduleSummaries.map((m) => (
                        <div key={m.dimension}>
                          <Typography.Text strong style={{ fontSize: 13 }}>
                            {m.name}（{m.score ?? '—'} 分）
                          </Typography.Text>
                          <div style={{ fontSize: 12.5, color: '#595959', lineHeight: 1.8 }}>
                            {m.issues.length > 0
                              ? m.issues
                                  .map((it) => `${it.questionNumber != null ? `第${it.questionNumber}题` : ''}「${it.indicator}」${it.score}分`)
                                  .join('、')
                              : '无明显短板'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ),
                },
              ]}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>🤖 由 AI 基于诊断数据生成，仅供参考</Typography.Text>
          </div>
        ) : (
          score.moduleSummaries.length === 0 ? (
            <Typography.Text type="secondary">恭喜！当前没有触发改进建议，继续保持</Typography.Text>
          ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {score.moduleSummaries.map((m) => (
            <div
              key={m.dimension}
              style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: '14px 16px', background: '#fafafa' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Typography.Text strong style={{ fontSize: 15 }}>
                  {m.name}
                </Typography.Text>
                {m.score !== null && (
                  <Tag color={m.score < 50 ? 'red' : m.score < 70 ? 'orange' : 'green'}>{m.score} 分</Tag>
                )}
              </div>
              {m.issues.length > 0 && (
                <div
                  style={{
                    background: '#fff7f0',
                    borderRadius: 8,
                    padding: '8px 12px',
                    marginBottom: 8,
                    fontSize: 13,
                    lineHeight: 1.7,
                  }}
                >
                  <b>问题所在：</b>
                  {m.issues.map((it) => `${it.indicator}（${it.score} 分）`).join('、')}
                  ，是「{m.name}」的主要短板，优先改进见效最快。
                </div>
              )}
              {m.suggestions.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {m.suggestions.map((s, i) => {
                    const p = PRIORITY_LABEL[s.priority];
                    return (
                      <div
                        key={`${s.questionCode}-${i}`}
                        style={{
                          border: '1px solid #ffd6e7',
                          background: '#fff0f6',
                          borderRadius: 8,
                          padding: '8px 12px',
                          fontSize: 13,
                          lineHeight: 1.7,
                        }}
                      >
                        <Tag color={p.color} style={{ marginRight: 8 }}>
                          {p.text}
                        </Tag>
                        <b>建议 {i + 1}</b>：{s.content}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
          )
        )}

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 16 }}
        message="本报告基于企业自评，可能存在高估，建议结合顾问访谈校准；5 分与 A 级 = 行业标杆水平。"
      />
    </Card>
  );
}
